import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import type {
  FpCodegenEvents,
  FpUserCancel,
  SerializableMcpConnection,
} from "../../../schemas/codegen";
import { useInputStore, useMessageStore } from "../../state/stores";
import { QUERY_KEYS } from "./queryKeys";
import { useActiveProjectId } from "./useActiveProjectId";
import { useActiveWebSocketConnection } from "./useActiveWebSocketConnection";
import { useFilesystemSync } from "./useFilesystemSync";
import { useMessageCacheSync } from "./useMessageCacheSync";
import { useMessageManagement } from "./useMessageManagement";
import { useProjectCreation } from "./useProjectCreation";
import { useSendProjectMessage } from "./useSendProjectMessage";

/**
 * Hook for WebSocket messaging operations and real-time message handling.
 *
 * Manages the WebSocket connection lifecycle, message handlers, and provides
 * messaging operations like sending messages and cancelling operations.
 *
 * @returns Object containing messaging operations and connection status
 *
 * When to use:
 * - In components that need to send messages, create chats, or cancel operations
 * - When you need access to connection status for UI states
 * - For components that handle user messaging interactions (like chat inputs)
 *
 * Do NOT use for:
 * - Displaying error notifications (use useCurrentWebSocketError instead)
 * - Simple error state access (access messageStore directly)
 */
export function useWebSocketMessaging() {
  const activeProjectId = useActiveProjectId();
  const sendMessage = useSendProjectMessage();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeProjectIdRef = useRef(activeProjectId);

  const {
    addMessage,
    updateMessage,
    appendMessageContent,
    addMessagePart,
    updateMessagePart,
  } = useMessageManagement();
  const {
    connection,
    status: connectionStatus,
    connect,
  } = useActiveWebSocketConnection(activeProjectId);
  const { createNewProject } = useProjectCreation();
  const { refetch: refetchMessages } = useMessageCacheSync(activeProjectId);
  const {
    handleFileCreated: handleFilesystemFileCreated,
    handleFileUpdated: handleFilesystemFileUpdated,
    handleSnapshotsUpdated: handleFilesystemSnapshotsUpdated,
    handleSnapshotCreated: handleFilesystemSnapshotCreated,
  } = useFilesystemSync();

  const clearInput = useInputStore((state) => state.clearInput);
  const appendStepLogs = useMessageStore((state) => state.appendStepLogs);
  const setWebSocketError = useMessageStore((state) => state.setWebSocketError);

  const cancelMessage = useCallback(
    (messageId: string) => {
      if (!activeProjectId) {
        console.error(
          "No active project ID for cancelling message: ",
          messageId,
        );
        return;
      }

      const messageData: FpUserCancel = {
        type: "user.cancel",
        messageId,
      };

      if (connection) {
        connection.send(messageData);
        refetchMessages();
      }
    },
    [activeProjectId, connection, refetchMessages],
  );

  const queryClient = useQueryClient();

  // Keep ref updated with current activeProjectId
  useEffect(() => {
    activeProjectIdRef.current = activeProjectId;
  }, [activeProjectId]);

  // Set up connection and message handlers when projectId changes
  useEffect(() => {
    if (!connection || !activeProjectId) {
      return;
    }

    connection.connect();
    clearInput();

    // Set up message handler
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        const eventData = data as FpCodegenEvents;

        switch (eventData.type) {
          case "agent.message.created": {
            const newMessage = {
              ...eventData.message,
              status:
                eventData.message.sender === "agent"
                  ? eventData.message.status || "processing"
                  : "completed",
            } as const;
            addMessage(newMessage);
            break;
          }
          case "agent.message.updated": {
            updateMessage(eventData.message.id, {
              ...eventData.message,
            });

            break;
          }
          case "agent.message.content.appended": {
            appendMessageContent(eventData.messageId, eventData.content);
            break;
          }
          case "agent.file.created": {
            handleFilesystemFileCreated({
              type: "agent.file.created",
              messageId: eventData.messageId,
              file: eventData.file,
            });
            break;
          }
          case "agent.file.updated": {
            handleFilesystemFileUpdated({
              type: "agent.file.updated",
              messageId: eventData.messageId,
              file: eventData.file,
            });
            break;
          }
          case "agent.version.created": {
            // TODO: Synchronize projectVersions (aka snapshots) with filesystem store
            // This should update the snapshots list and filesBySnapshotId when new versions are created
            console.log("Version created:", eventData);
            break;
          }
          case "agent.error": {
            if (eventData.messageId) {
              updateMessage(eventData.messageId, {
                status: "cancelled",
              });
              refetchMessages();
            }
            setWebSocketError(eventData);
            break;
          }

          case "secret.mutated": {
            // Refetch secrets for this projectId
            if (activeProjectId) {
              queryClient.refetchQueries({
                queryKey: [QUERY_KEYS.SECRETS, activeProjectId],
              });
            }
            break;
          }

          case "mcp.servers.updated": {
            // Invalidate MCP servers for this activeProjectId
            if (activeProjectId) {
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MCP_SERVERS, activeProjectId],
              });
            }
            break;
          }

          case "mcp.server.add.failed": {
            // Add failed server to MCP servers cache so it shows in UI with error immediately
            if (activeProjectId) {
              queryClient.setQueryData(
                [QUERY_KEYS.MCP_SERVERS, activeProjectId],
                (
                  old: Record<string, SerializableMcpConnection> | undefined,
                ) => {
                  const failedId = `failed-${Date.now()}`;
                  return {
                    ...old,
                    [failedId]: {
                      id: failedId,
                      url: eventData.url,
                      name: eventData.name,
                      connectionState: "failed" as const,
                      error: eventData.error,
                      createdAt: new Date(),
                    },
                  };
                },
              );

              // Then invalidate to get the latest actual state from server
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.MCP_SERVERS, activeProjectId],
              });
            }
            break;
          }

          case "agent.message.part.added": {
            addMessagePart(
              eventData.messageId,
              eventData.part,
              eventData.index,
            );
            break;
          }

          case "agent.message.part.updated": {
            updateMessagePart(
              eventData.messageId,
              eventData.partId,
              eventData.updates as {
                content?: string;
                status?:
                  | "streaming"
                  | "complete"
                  | "pending"
                  | "executing"
                  | "error";
                result?: unknown;
              },
            );

            // Check if this is a deployment tool that just completed
            if (eventData.updates.status === "complete") {
              // Get the part from message store to check if it's a deploy tool
              const messageStore = useMessageStore.getState();
              const message = messageStore.messages.find(
                (m) => m.id === eventData.messageId,
              );
              const part = message?.parts?.find(
                (p) => p.id === eventData.partId,
              );

              // Only invalidate endpoints cache if this is a deploy tool completion
              if (part?.type === "tool-call" && part.toolName === "deploy") {
                // Clear any existing timeout
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                }

                // Wait 10 seconds for Cloudflare worker deployment to propagate
                timeoutRef.current = setTimeout(() => {
                  // Get fresh activeProjectId at execution time to avoid stale closure
                  const currentActiveProjectId = activeProjectIdRef.current;
                  if (currentActiveProjectId) {
                    queryClient.refetchQueries({
                      queryKey: [QUERY_KEYS.ENDPOINTS, currentActiveProjectId],
                    });
                  }
                  timeoutRef.current = null;
                }, 10000);
              }
            }
            break;
          }

          case "tasks.created": {
            // Invalidate tasks for the specific message and project
            if (activeProjectId) {
              // Also invalidate the general tasks query for this project
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.TASKS, activeProjectId],
              });
            }
            break;
          }

          case "task.updated": {
            // Invalidate tasks for the specific message and project
            if (activeProjectId) {
              // Also invalidate the general tasks query for this project
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.TASKS, activeProjectId],
              });
            }
            break;
          }

          case "deployment.updated": {
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.DEPLOYMENTS, eventData.deploymentId],
            });
            break;
          }

          case "deployment.log.append": {
            // Directly append logs to the deployment logs store for real-time updates
            appendStepLogs(
              eventData.deploymentId,
              eventData.id,
              eventData.logs,
            );
            break;
          }

          case "filesystem.snapshots.updated": {
            handleFilesystemSnapshotsUpdated(eventData);
            break;
          }

          case "filesystem.snapshot.created": {
            handleFilesystemSnapshotCreated(eventData);
            break;
          }

          case "runtime.error": {
            console.log("Runtime error received:", eventData.data);
            // TODO: Add UI handling for runtime errors
            // For now, just log the error - implement UI display later
            break;
          }
        }
      } catch (error) {
        console.error("Error processing WebSocket message:", error);

        setWebSocketError({
          type: "agent.error",
          error: error instanceof Error ? error.message : "Unknown error",
          level: "error",
        });
        refetchMessages();
      }
    };

    connection.addMessageHandler(handleMessage);

    return () => {
      connection.removeMessageHandler(handleMessage);
      // Clear any pending timeout to prevent memory leaks
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    activeProjectId,
    connection,
    handleFilesystemFileCreated,
    handleFilesystemFileUpdated,
    handleFilesystemSnapshotsUpdated,
    handleFilesystemSnapshotCreated,
    setWebSocketError,
    refetchMessages,
    addMessage,
    updateMessage,
    appendMessageContent,
    addMessagePart,
    updateMessagePart,
    clearInput,
    queryClient,
    appendStepLogs,
  ]);

  return {
    sendMessage,
    cancelMessage,
    connectionStatus,
    createNewProject,
    reconnect: connect,
  };
}
